/**
 * DataEntryInsights — the agent's read of the whole file (ADR-0037, Turbo's DataEntryInsights):
 * what it is, the numbers, what needs attention. Every warning is a filter: clicking it walks only
 * through the rows it talks about. Suggested rules turn a mixed file into kinds in one click.
 */
import { useState } from "react";
import { Button, IconButton, Select, Tag } from "@NP-Webber/crm-kit/dist/next/index.js";
import type { AppliedRuleDto, FactDto, InsightsDto, RowKind, RuleThen, RuleWhen, SuggestedRuleDto } from "../../../api/aiDataEntryApi";
import { AiFeedbackButtons } from "../feedback/AiFeedbackButtons";
import { KIND_META, KIND_ORDER, money, shortDate } from "./dataEntryModel";

const LEVEL_TONE = { danger: "danger", warning: "warning", info: "info", success: "success", neutral: "neutral" } as const;
const LEVEL_ICON = { danger: "alert", warning: "alert", info: "info", success: "check", neutral: "eyeOff" } as const;
const RULE_FIELD_LABEL: Record<string, string> = { category: "הקטגוריה בקובץ", type: "הסוג בקובץ" };

function factValue(f: FactDto) {
  if (f.kind === "money") return money(Number(f.value));
  if (f.kind === "range") {
    const [a, b] = String(f.value).split(" – ");
    return `${shortDate(a)} – ${shortDate(b)}`;
  }
  return Number(f.value).toLocaleString("he-IL");
}

export type ApplyRule = (rule: { when: RuleWhen; then: RuleThen }, opts: { save: boolean }) => void;

function SuggestedRule({ rule, kinds, onApplyRule }: { rule: SuggestedRuleDto; kinds: RowKind[]; onApplyRule: ApplyRule }) {
  const [kind, setKind] = useState<RowKind>(rule.then.kind);
  const apply = (save: boolean) => onApplyRule({ when: rule.when, then: { kind } }, { save });
  return (
    <li>
      <span>
        <b>«{rule.when.value}»</b>{" "}
        <small className="de-muted">
          · {rule.rows} שורות{rule.agreement < 100 ? ` · הסוכן מתלבט (${rule.agreement}%)` : ""}
        </small>
      </span>
      <Select
        value={kind}
        options={KIND_ORDER.filter((k) => kinds.includes(k)).map((k) => ({ value: k, label: KIND_META[k].label }))}
        onChange={(v) => v && setKind(v as RowKind)}
        aria-label={`סוג לקבוצה ${rule.when.value}`}
      />
      <Button size="sm" onClick={() => apply(false)}>
        להחיל על הקובץ
      </Button>
      <Button size="sm" variant="primary" onClick={() => apply(true)}>
        ולשמור כלל
      </Button>
    </li>
  );
}

function RulesSection({
  rules,
  suggestedRules,
  kinds,
  onApplyRule,
  onRemoveRule,
}: {
  rules: AppliedRuleDto[];
  suggestedRules: SuggestedRuleDto[];
  kinds: RowKind[];
  onApplyRule: ApplyRule;
  onRemoveRule: (r: AppliedRuleDto) => void;
}) {
  const active = rules.filter((r) => r.hits || !r.saved);
  if (!active.length && !suggestedRules.length) return null;
  return (
    <div className="de-ins__rules">
      {active.length ? (
        <>
          <b>כללים שהוחלו</b>
          <ul className="de-rules">
            {active.map((r) => (
              <li key={`${r.id ?? "f"}-${r.label}`}>
                <Tag tone="violet" icon="sliders">
                  {r.hits ?? 0} שורות
                </Tag>
                <span>
                  {r.label}
                  {r.saved ? <small className="de-muted"> · שמור</small> : null}
                </span>
                <IconButton size="sm" icon="x" label="הסרת הכלל" onClick={() => onRemoveRule(r)} />
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {suggestedRules.length ? (
        <>
          <b>הקובץ מעורב — מה כל קבוצה?</b>
          <span className="de-muted">
            הסוכן קיבץ את השורות לפי {RULE_FIELD_LABEL[suggestedRules[0]!.when.field] ?? "ערך בקובץ"}. בחרו לכל קבוצה את הסוג — והוא יוחל על כל השורות שלה.
          </span>
          <ul className="de-rules">
            {suggestedRules.map((r) => (
              <SuggestedRule key={r.label} rule={r} kinds={kinds} onApplyRule={onApplyRule} />
            ))}
          </ul>
          <span className="de-muted">גם שינוי סוג או קטגוריה בשורה אחת — הסוכן יציע להחיל אותו על כל השורות הדומות.</span>
        </>
      ) : null}
    </div>
  );
}

export default function DataEntryInsights({
  sessionId,
  insights,
  activeFlag,
  onFlag,
  onRecheck,
  rules,
  suggestedRules,
  kinds,
  onApplyRule,
  onRemoveRule,
  busy,
}: {
  sessionId: string;
  insights: InsightsDto;
  activeFlag: string | null;
  onFlag: (flag: string | null) => void;
  onRecheck: () => void;
  rules: AppliedRuleDto[];
  suggestedRules: SuggestedRuleDto[];
  kinds: RowKind[];
  onApplyRule: ApplyRule;
  onRemoveRule: (r: AppliedRuleDto) => void;
  busy?: boolean;
}) {
  const { profile, facts, warnings, recommendations, narrative } = insights;
  const recs = narrative?.recommendations?.length ? narrative.recommendations : recommendations;
  const period = facts.find((f) => f.key === "period");
  return (
    <section className="de-ins is-open is-embedded" aria-label="ניתוח הקובץ">
      <div className="de-ins__h">
        <span className="de-ins__ic" aria-hidden="true">
          ✦
        </span>
        <b>ניתוח הקובץ</b>
        <Tag tone="primary">{profile.label}</Tag>
        <span className="de-muted de-ins__sub">
          {profile.text}
          {period ? ` · ${factValue(period)}` : ""}
        </span>
      </div>
      <div className="de-ins__b">
        {narrative?.summary ? (
          <>
            <p className="de-ins__summary">{narrative.summary}</p>
            <AiFeedbackButtons
              surface="data_entry"
              refKey={`${sessionId}:insights`}
              question={`ניתוח קובץ: ${profile.label} — ${profile.text}`}
              answer={[narrative.summary, ...narrative.recommendations.map((r) => `• ${r}`)].join("\n")}
            />
          </>
        ) : null}
        <div className="de-ins__facts">
          {facts.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`de-fact ${f.flag ? "is-link" : ""} ${f.tone ? `de-fact--${f.tone}` : ""} ${activeFlag && activeFlag === f.flag ? "is-on" : ""}`}
              disabled={!f.flag}
              onClick={() => f.flag && onFlag(activeFlag === f.flag ? null : f.flag)}
            >
              <span className="de-fact__v">{factValue(f)}</span>
              <span className="de-fact__l">{f.label}</span>
            </button>
          ))}
        </div>
        {warnings.length ? (
          <ul className="de-ins__w">
            {warnings.map((w, i) => (
              <li key={`${w.flag ?? "x"}-${i}`}>
                <button
                  type="button"
                  className={`de-warn de-warn--${w.level} ${w.flag && activeFlag === w.flag ? "is-on" : ""}`}
                  disabled={!w.flag}
                  onClick={() => w.flag && onFlag(activeFlag === w.flag ? null : w.flag)}
                  title={w.flag ? "הצגת השורות האלה בלבד" : undefined}
                >
                  <Tag tone={LEVEL_TONE[w.level]} icon={LEVEL_ICON[w.level]}>
                    {w.count}
                  </Tag>
                  <span>{w.text.replace(/^\d+\s/, "")}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <RulesSection rules={rules} suggestedRules={suggestedRules} kinds={kinds} onApplyRule={onApplyRule} onRemoveRule={onRemoveRule} />
        {recs.length ? (
          <div className="de-ins__recs">
            <b>המלצות</b>
            <ul>
              {recs.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </div>
        ) : null}
        <div className="de-ins__foot">
          {activeFlag ? (
            <Button size="sm" icon="x" onClick={() => onFlag(null)}>
              ביטול הסינון — חזרה לכל השורות לטיפול
            </Button>
          ) : null}
          <Button size="sm" icon="refresh" disabled={busy} onClick={onRecheck}>
            בדיקה מחדש מול המערכת
          </Button>
        </div>
      </div>
    </section>
  );
}
